import React, { useState } from "react";
import { InfoCircleOutlined, LineOutlined } from "@ant-design/icons";
import "./MapLayersDrawer.less";
import { Flex } from "antd";
import satelliteIcon from "../../../assets/images/autozones/satellite_alt.svg";
import agriIcon from "../../../assets/images/autozones/agriculture.svg";
import mapsIcon from "../../../assets/images/autozones/add-map.svg";
import AutozonesComponent from "../../autozones";
import MachineryButtonComponent from "../../../shared/mfe/MachineryButton";
import { useNewTrialState } from "../hooks/NewTrialStateProvider";
import SoilButtonComponent from "../../../shared/sl/SoilButton";
import { ReactComponent as ExpandIcon } from "../../../assets/images/open_in_full.svg";
import { ReactComponent as MapLayerMenu } from "../../../assets/images/map-drawer-menu.svg";
import { ReactComponent as NavLayerBtn } from "../../../assets/images/btn-nav-layers.svg";
import { AUTOZONE_REQUEST_STATUS } from "../../autozones/types";

// Types
type OverlayType = "layers" | "satellite" | "agriculture" | "map";
type LayerSubtype = "subtype1" | "subtype2";
type LayerSoilL = "subtype1" | "subtype2";

const MapLayersDrawer: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSoilDrawerOpen, setIsSoilDrawerOpen] = useState(false);
  const [isAutozoneDrawerOpen, setIsAutozoneDrawerOpen] = useState(false);
  const [isMachineryDrawerOpen, setIsMachineryDrawerOpen] = useState(false);
  const [activeOverlay, setActiveOverlay] = useState<OverlayType | null>(
    "layers"
  );
  const [selectedSubtypes, setSelectedSubtypes] = useState<LayerSubtype[]>([]);
  const {
    autozoneRequestStatus,
    setAutozoneOpacity,
    setAutozoneRequestStatus,
  } = useNewTrialState();

  const resetAutozoneErrorStatus = (): void => {
    if (autozoneRequestStatus === AUTOZONE_REQUEST_STATUS.ERROR) {
      setAutozoneRequestStatus(AUTOZONE_REQUEST_STATUS.DEFAULT);
    }
  };

  const toggleDrawer = (): void => {
    if (isExpanded) {
      resetAutozoneErrorStatus();
    }
    setIsExpanded(!isExpanded);
  };
  const [selectedSoilL, setselectedSoilL] = useState<LayerSubtype[]>([]);
  const handleOverlayClick = (type: OverlayType): void => {
    if (type === "layers") {
      setActiveOverlay(type);
    }
  };

  const handleSubtypeClick = (subtype: LayerSubtype): void => {
    if (selectedSubtypes.includes(subtype)) {
      // setSelectedSubtypes(selectedSubtypes.filter(st => st !== subtype));
    } else {
      setSelectedSubtypes([...selectedSubtypes, subtype]);
      // Reset autozoneOpacity to default when re-selecting autozones
      if (subtype === "subtype1") {
        setAutozoneOpacity(60);
      }
    }
  };

  const handleSoilLClick = (subtype: LayerSoilL): void => {
    if (selectedSoilL.includes(subtype)) {
      // setSelectedSubtypes(selectedSubtypes.filter(st => st !== subtype));
    } else {
      setselectedSoilL([...selectedSoilL, subtype]);
    }
  };

  const removeSubtype = (subtype: LayerSubtype): void => {
    setSelectedSubtypes(selectedSubtypes.filter((st) => st !== subtype));
  };

  // Close only the drawer, keep soil layer selected so close icon remains visible on SoilButton.
  const handleCloseSoilDrawer = (): void => {
    setIsSoilDrawerOpen(false);
  };

  const handleClearSoilSelection = (): void => {
    setselectedSoilL(selectedSoilL.filter((st) => st !== "subtype2"));
    setIsSoilDrawerOpen(false);
  };

  const handleCloseAutozoneDrawer = (): void => {
    setIsAutozoneDrawerOpen(false);
    resetAutozoneErrorStatus();
  };

  const handleCloseMachineryDrawer = (): void => {
    setIsMachineryDrawerOpen(false);
  };

  const handleOpenSoilDrawer = (): void => {
    setIsSoilDrawerOpen(true);
    setIsAutozoneDrawerOpen(false);
    setIsMachineryDrawerOpen(false);
  };

  const handleOpenAutozoneDrawer = (): void => {
    setIsAutozoneDrawerOpen(true);
    setIsSoilDrawerOpen(false);
    setIsMachineryDrawerOpen(false);
  };

  const handleOpenMachineryDrawer = (): void => {
    setIsMachineryDrawerOpen(true);
    setIsSoilDrawerOpen(false);
    setIsAutozoneDrawerOpen(false);
  };

  const handleClearAutozoneSelection = (): void => {
    setSelectedSubtypes(selectedSubtypes.filter((st) => st !== "subtype1"));
    setAutozoneOpacity(0);
    setIsAutozoneDrawerOpen(false);
    resetAutozoneErrorStatus();
  };

  const handleClearMachinerySelection = (): void => {
    setSelectedSubtypes(selectedSubtypes.filter((st) => st !== "subtype2"));
    setIsMachineryDrawerOpen(false);
  };

  const isSoilSelected = selectedSoilL.includes("subtype2");
  const isAutozoneSelected = selectedSubtypes.includes("subtype1");
  const isMachinerySelected = selectedSubtypes.includes("subtype2");

  // Function to add soil image layer to the map
  return (
    <>
      <div
        className={`drawer${
          isExpanded ||
          isSoilDrawerOpen ||
          isAutozoneDrawerOpen ||
          isMachineryDrawerOpen
            ? " expanded"
            : ""
        }${isSoilDrawerOpen ? " soil-open" : ""}${
          isAutozoneDrawerOpen ? " autozone-open" : ""
        }${isMachineryDrawerOpen ? " machinery-open" : ""}`}
      >
        <div style={{ width: "100%" }} className="drawer-header">
          <button
            style={{ width: "100%" }}
            className="toggle-button"
            onClick={toggleDrawer}
          >
            {isExpanded ? (
              <Flex justify="space-between" align="center" gap={4}>
                hide
                <span>
                  <LineOutlined style={{ fontSize: "14px" }} />
                </span>
              </Flex>
            ) : (
              <Flex justify="space-between" align="center" gap={4}>
                expand{" "}
                <span className="expand-icon">
                  <ExpandIcon />
                </span>
              </Flex>
            )}
          </button>
        </div>
        {isExpanded ? (
          <>
            {" "}
            <div
              className={`drawer-content ${
                isExpanded ? "drawer-expanded" : ""
              }`}
            >
              <div className="overlay-types">
                <NavLayerBtn
                  className={`overlay-button ${
                    activeOverlay === "layers" ? "active" : ""
                  }`}
                  onClick={() => {
                    handleOverlayClick("layers");
                  }}
                />
                {activeOverlay === "layers" && (
                  <div className="subtype-container">
                    <Flex gap={4} align="center" justify="center">
                      <div
                        className={`soilype-button ${
                          selectedSoilL.includes("subtype2") ? "selected" : ""
                        }`}
                        onClick={() => {
                          handleSoilLClick("subtype2");
                        }}
                      >
                        <SoilButtonComponent
                          onOpen={handleOpenSoilDrawer}
                          onClose={handleCloseSoilDrawer}
                          onClearSelection={handleClearSoilSelection}
                          isSelected={isSoilSelected}
                        />
                      </div>
                    </Flex>
                    <Flex
                      vertical={true}
                      gap={4}
                      align="center"
                      justify="center"
                    >
                      <div
                        className={`subtype-button ${
                          selectedSubtypes.includes("subtype1")
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          handleSubtypeClick("subtype1");
                        }}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <AutozonesComponent
                            onOpen={handleOpenAutozoneDrawer}
                            onClose={handleCloseAutozoneDrawer}
                            onClearSelection={handleClearAutozoneSelection}
                            isSelected={isAutozoneSelected}
                          />
                        </div>
                      </div>
                    </Flex>
                    <Flex
                      vertical={true}
                      gap={4}
                      align="center"
                      justify="center"
                    >
                      <div
                        className={`subtype-button ${
                          selectedSubtypes.includes("subtype2")
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          handleSubtypeClick("subtype2");
                        }}
                      >
                        <MachineryButtonComponent
                          onOpen={handleOpenMachineryDrawer}
                          onClose={handleCloseMachineryDrawer}
                          onClearSelection={handleClearMachinerySelection}
                          isSelected={isMachinerySelected}
                        />
                      </div>
                    </Flex>
                  </div>
                )}
              </div>
              <InfoCircleOutlined className="info-icon-soil" />
            </div>
          </>
        ) : (
          <Flex vertical={true} gap={4} align="center" justify="center">
            <div>
              {activeOverlay === "layers" && <MapLayerMenu />}
              {activeOverlay === "satellite" && (
                <img src={satelliteIcon} alt="Satellite" />
              )}
              {activeOverlay === "agriculture" && (
                <img src={agriIcon} alt="Agriculture" />
              )}
              {activeOverlay === "map" && <img src={mapsIcon} alt="Map" />}
            </div>
            <Flex vertical={true} gap={4} align="center" justify="center">
              {selectedSubtypes.includes("subtype1") && (
                <div className="subtype-button">
                  <AutozonesComponent
                    onOpen={handleOpenAutozoneDrawer}
                    onClose={handleCloseAutozoneDrawer}
                    onClearSelection={handleClearAutozoneSelection}
                    isSelected={isAutozoneSelected}
                  />
                </div>
              )}
              {selectedSubtypes.includes("subtype2") && (
                <div className="subtype-button">
                  <MachineryButtonComponent
                    onOpen={handleOpenMachineryDrawer}
                    onClose={handleCloseMachineryDrawer}
                    onClearSelection={handleClearMachinerySelection}
                    isSelected={isMachinerySelected}
                  />
                </div>
              )}
            </Flex>
          </Flex>
        )}
      </div>
    </>
  );
};

export default MapLayersDrawer;
